import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Search,
  FilterList,
  Visibility,
  LocationOn,
  CalendarToday,
  AttachMoney,
} from '@mui/icons-material';
import api from '../services/api';

const Sales = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const perPage = 9;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    start_date: '',
    end_date: '',
    setup_date: '',
    estimated_value: '',
    commission_rate: '',
    client_id: '',
    status: 'draft',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchSales();
  }, [page, statusFilter, startDateFilter, endDateFilter]);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (startDateFilter) {
        params.start_date = startDateFilter;
      }
      if (endDateFilter) {
        params.end_date = endDateFilter;
      }

      const response = await api.get('/sales/', { params });
      const data = response.data;

      setSales(data?.sales || data || []);
      setTotalSales(data?.total || 0);
      setTotalPages(Math.ceil((data?.total || 0) / perPage));
    } catch (error) {
      console.error('Error fetching sales:', error);
      showSnackbar('Failed to fetch sales', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, sale) => {
    setAnchorEl(event.currentTarget);
    setSelectedSale(sale);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenModal = (sale = null) => {
    if (sale) {
      setSelectedSale(sale);
      setFormData({
        name: sale.name || '',
        description: sale.description || '',
        address: sale.address || '',
        city: sale.city || '',
        state: sale.state || '',
        zip_code: sale.zip_code || '',
        county: sale.county || '',
        start_date: sale.start_date ? sale.start_date.split('T')[0] : '',
        end_date: sale.end_date ? sale.end_date.split('T')[0] : '',
        setup_date: sale.setup_date ? sale.setup_date.split('T')[0] : '',
        estimated_value: sale.estimated_value || '',
        commission_rate: sale.commission_rate || '',
        client_id: sale.client_id || '',
        status: sale.status || 'draft',
      });
    } else {
      setSelectedSale(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        county: '',
        start_date: '',
        end_date: '',
        setup_date: '',
        estimated_value: '',
        commission_rate: '',
        client_id: '',
        status: 'draft',
      });
    }
    setFormErrors({});
    setOpenModal(true);
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSale(null);
    setFormData({
      name: '',
      description: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      county: '',
      start_date: '',
      end_date: '',
      setup_date: '',
      estimated_value: '',
      commission_rate: '',
      client_id: '',
      status: 'draft',
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.address?.trim()) errors.address = 'Address is required';
    if (!formData.city?.trim()) errors.city = 'City is required';
    if (!formData.state?.trim()) errors.state = 'State is required';
    if (!formData.zip_code?.trim()) errors.zip_code = 'ZIP code is required';
    if (formData.commission_rate && (formData.commission_rate < 0 || formData.commission_rate > 100)) {
      errors.commission_rate = 'Commission rate must be between 0 and 100';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = { ...formData };

      // Convert empty strings to null for optional fields
      if (!submitData.client_id) submitData.client_id = null;
      if (!submitData.estimated_value) submitData.estimated_value = null;
      if (!submitData.commission_rate) submitData.commission_rate = null;
      if (!submitData.setup_date) submitData.setup_date = null;

      if (selectedSale) {
        await api.put(`/sales/${selectedSale.id}`, submitData);
        showSnackbar('Sale updated successfully', 'success');
      } else {
        await api.post('/sales/', submitData);
        showSnackbar('Sale created successfully', 'success');
      }

      handleCloseModal();
      fetchSales();
    } catch (error) {
      console.error('Error saving sale:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to save sale';
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleOpenDeleteDialog = (sale) => {
    setSelectedSale(sale);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedSale(null);
  };

  const handleDelete = async () => {
    if (!selectedSale) return;

    try {
      await api.delete(`/sales/${selectedSale.id}`);
      showSnackbar('Sale deleted successfully', 'success');
      handleCloseDeleteDialog();
      fetchSales();
    } catch (error) {
      console.error('Error deleting sale:', error);
      showSnackbar('Failed to delete sale', 'error');
    }
  };

  const handleViewDetails = (saleId) => {
    navigate(`/sales/${saleId}`);
    handleMenuClose();
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      scheduled: 'info',
      published: 'primary',
      in_progress: 'warning',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSales = sales.filter(sale =>
    sale.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sale.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sale.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Estate Sales
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your estate sales and track performance
          </Typography>
        </div>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
          New Sale
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, address, or city..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date From"
                value={startDateFilter}
                onChange={(e) => {
                  setStartDateFilter(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date To"
                value={endDateFilter}
                onChange={(e) => {
                  setEndDateFilter(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredSales.length} of {totalSales} sales
        </Typography>
      </Box>

      {/* Sales Grid */}
      <Grid container spacing={3}>
        {filteredSales.length > 0 ? (
          filteredSales.map((sale) => (
            <Grid item xs={12} md={6} lg={4} key={sale.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box flex={1} mr={1}>
                      <Typography variant="h6" fontWeight="bold" noWrap>
                        {sale.name || 'Untitled Sale'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={sale.status || 'draft'}
                        color={getStatusColor(sale.status)}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, sale)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {sale.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={2}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {sale.description}
                    </Typography>
                  )}

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {sale.address}, {sale.city}, {sale.state} {sale.zip_code}
                      </Typography>
                    </Box>

                    {sale.start_date && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(sale.start_date).toLocaleDateString()} - {' '}
                          {sale.end_date ? new Date(sale.end_date).toLocaleDateString() : 'TBD'}
                        </Typography>
                      </Box>
                    )}

                    {sale.estimated_value && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <AttachMoney fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Est. Value: ${Number(sale.estimated_value).toLocaleString()}
                          {sale.commission_rate && ` (${sale.commission_rate}% commission)`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchTerm || statusFilter !== 'all' ? 'No sales found' : 'No sales yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {searchTerm || statusFilter !== 'all'
                    ? 'Try adjusting your filters or search criteria'
                    : 'Create your first estate sale to get started'}
                </Typography>
                {!searchTerm && statusFilter === 'all' && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
                    Create First Sale
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedSale && handleViewDetails(selectedSale.id)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleOpenModal(selectedSale)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleOpenDeleteDialog(selectedSale)}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSale ? 'Edit Sale' : 'Create New Sale'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sale Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  error={!!formErrors.address}
                  helperText={formErrors.address}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  error={!!formErrors.city}
                  helperText={formErrors.city}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  error={!!formErrors.state}
                  helperText={formErrors.state}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.zip_code}
                  onChange={(e) => handleFormChange('zip_code', e.target.value)}
                  error={!!formErrors.zip_code}
                  helperText={formErrors.zip_code}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="County"
                  value={formData.county}
                  onChange={(e) => handleFormChange('county', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleFormChange('status', e.target.value)}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Setup Date"
                  value={formData.setup_date}
                  onChange={(e) => handleFormChange('setup_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(e) => handleFormChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={formData.end_date}
                  onChange={(e) => handleFormChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Estimated Value"
                  value={formData.estimated_value}
                  onChange={(e) => handleFormChange('estimated_value', e.target.value)}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Commission Rate"
                  value={formData.commission_rate}
                  onChange={(e) => handleFormChange('commission_rate', e.target.value)}
                  error={!!formErrors.commission_rate}
                  helperText={formErrors.commission_rate}
                  InputProps={{ endAdornment: '%' }}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Client ID"
                  value={formData.client_id}
                  onChange={(e) => handleFormChange('client_id', e.target.value)}
                  helperText="Optional: Link to client record"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedSale ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Sale</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedSale?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Sales;
